import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Logger from "../../services/Logger";
import AdminJigsawPlayService from "./play/AdminJigsawPlayService";

function playerLabel(row) {
    if (!row) return "";
    if (row.email && row.email !== "—") return row.email;
    if (row.name && row.name !== "—") return row.name;
    return "Guest";
}

export default function JigsawPlaySummaryRow({ storyId }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [completed, setCompleted] = useState(0);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        if (!storyId) return undefined;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const [all, done] = await Promise.all([
                    AdminJigsawPlayService.listPlays({ storyId, pageSize: 6, sort: "startedAt:desc" }),
                    AdminJigsawPlayService.listPlays({
                        storyId,
                        pageSize: 1,
                        isCompleted: true,
                        sort: "startedAt:desc",
                    }),
                ]);
                if (cancelled) return;
                setTotal(Number(all.total) || 0);
                setCompleted(Number(done.total) || 0);
                setRecent(Array.isArray(all.rows) ? all.rows : []);
            } catch (err) {
                Logger.error("Failed to load jigsaw play summary:", err);
                if (!cancelled) {
                    setTotal(0);
                    setCompleted(0);
                    setRecent([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [storyId]);

    const inProgress = Math.max(0, total - completed);
    const last = recent[0] || null;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "nowrap",
                overflowX: "auto",
                minHeight: 44,
                px: 0.5,
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                Play logs
            </Typography>
            {loading ? (
                <CircularProgress size={16} />
            ) : (
                <>
                    <Chip size="small" label={`${total} play${total === 1 ? "" : "s"}`} />
                    <Chip size="small" color="success" variant="outlined" label={`${completed} done`} />
                    <Chip size="small" variant="outlined" label={`${inProgress} in progress`} />
                    {last ? (
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                            Last: {playerLabel(last)}
                            {last.startedAt ? ` · ${last.startedAt}` : ""}
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            No plays yet
                        </Typography>
                    )}
                    {recent.slice(0, 4).map((row) => (
                        <Tooltip key={row.id} title={`${row.status}${row.startedAt ? ` · ${row.startedAt}` : ""}`}>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={playerLabel(row)}
                                onClick={() => navigate(`/admin/jigsaw/play/${row.id}`)}
                                sx={{ maxWidth: 180 }}
                            />
                        </Tooltip>
                    ))}
                </>
            )}
            {storyId ? (
                <Button
                    size="small"
                    onClick={() => navigate(`/admin/jigsaw/browse/${encodeURIComponent(storyId)}`)}
                    sx={{ textTransform: "none", minHeight: 44, flexShrink: 0, ml: "auto" }}
                >
                    View all
                </Button>
            ) : null}
        </Box>
    );
}
